import { capitalReturnEntries, priceRedemptionLines } from '../../domain/lifecycleCalc';
import { unitsAt } from '../../domain/holdings';
import type { RegistryState } from '../../data/types';
import { money } from '../../lib/format';
import type { RegistryAction } from '../actions';
import {
  actorName,
  addApproval,
  addCommunication,
  addDocuments,
  appendLedger,
  fundCode,
  raiseHeldPayments,
  today,
  withAudit,
} from '../helpers';

type Handler<T extends RegistryAction['type']> = (
  state: RegistryState,
  action: Extract<RegistryAction, { type: T }>,
) => RegistryState;

export const saveCapitalReturn: Handler<'CAPITAL_RETURN_SAVE'> = (state, action) => {
  const exists = state.capitalReturns.some((item) => item.id === action.capitalReturn.id);
  const capitalReturns = exists
    ? state.capitalReturns.map((item) =>
        item.id === action.capitalReturn.id ? action.capitalReturn : item,
      )
    : [action.capitalReturn, ...state.capitalReturns];
  return withAudit(
    { ...state, capitalReturns },
    action.capitalReturn.kind === 'FINAL' ? 'Wind-up drafted' : 'Capital return drafted',
    fundCode(state, action.capitalReturn.fundId),
    `${action.capitalReturn.returnPct}% of capital to ${action.capitalReturn.lines.length} holders`,
  );
};

export const submitCapitalReturn: Handler<'CAPITAL_RETURN_SUBMIT'> = (state, action) => {
  const capitalReturn = state.capitalReturns.find((item) => item.id === action.id);
  if (!capitalReturn) return state;
  const total = capitalReturn.lines.reduce((sum, line) => sum + line.amount + line.finalIncome, 0);
  let next: RegistryState = {
    ...state,
    capitalReturns: state.capitalReturns.map((item) =>
      item.id === action.id
        ? { ...item, status: 'PENDING_APPROVAL', createdBy: actorName(state) }
        : item,
    ),
  };
  next = addApproval(next, {
    kind: 'CAPITAL_RETURN',
    refId: capitalReturn.id,
    fundId: capitalReturn.fundId,
    title: `${fundCode(state, capitalReturn.fundId)} ${capitalReturn.kind === 'FINAL' ? 'final capital return and wind-up' : `${capitalReturn.returnPct}% capital return`}`,
    summary: `${capitalReturn.reason} ${capitalReturn.lines.length} holders, payable ${capitalReturn.paymentDate}.`,
    amount: total,
  });
  return withAudit(
    next,
    'Capital return submitted for approval',
    fundCode(state, capitalReturn.fundId),
    money(total),
  );
};

export const payCapitalReturn: Handler<'CAPITAL_RETURN_PAY'> = (state, action) => {
  const capitalReturn = state.capitalReturns.find((item) => item.id === action.id);
  if (!capitalReturn || capitalReturn.status !== 'APPROVED') return state;
  const paid = { ...capitalReturn, status: 'PAID' as const, paymentDate: today };
  const code = fundCode(state, paid.fundId);
  let next = appendLedger(
    {
      ...state,
      capitalReturns: state.capitalReturns.map((item) => (item.id === paid.id ? paid : item)),
    },
    capitalReturnEntries(paid, 1, actorName(state)),
  );
  next = raiseHeldPayments(
    next,
    paid.fundId,
    `${code} ${paid.kind === 'FINAL' ? 'final' : 'partial'} capital return`,
    paid.lines.map((line) => ({ holderId: line.holderId, amount: line.amount + line.finalIncome })),
  );
  if (paid.kind === 'FINAL') {
    next = {
      ...next,
      funds: next.funds.map((fund) =>
        fund.id === paid.fundId ? { ...fund, status: 'WOUND_UP' } : fund,
      ),
    };
  }
  next = addDocuments(
    next,
    paid.lines.flatMap((line) => [
      {
        holderId: line.holderId,
        fundId: paid.fundId,
        kind: 'CAPITAL_RETURN_ADVICE' as const,
        name: `${code} capital return advice`,
        date: today,
        sizeKb: 92,
        visibleToInvestor: true,
        refId: paid.id,
      },
      ...(paid.kind === 'FINAL'
        ? [
            {
              holderId: line.holderId,
              fundId: paid.fundId,
              kind: 'EXIT_STATEMENT' as const,
              name: `${code} exit statement`,
              date: today,
              sizeKb: 118,
              visibleToInvestor: true,
              refId: paid.id,
            },
          ]
        : []),
    ]),
  );
  next = addCommunication(next, {
    template: paid.kind === 'FINAL' ? 'WIND_UP_NOTICE' : 'CAPITAL_RETURN_NOTICE',
    subject:
      paid.kind === 'FINAL'
        ? `${code} has repaid: your capital has been returned`
        : `${code}: ${paid.returnPct}% of your capital has been returned`,
    audience: `${code} holders`,
    fundId: paid.fundId,
    holderIds: paid.lines.map((line) => line.holderId),
    channels: ['EMAIL', 'PUSH'],
  });
  return withAudit(
    next,
    paid.kind === 'FINAL' ? 'Fund wound up' : 'Capital returned',
    code,
    `${paid.lines.length} payments released; advices published`,
  );
};

export const priceRedemptionRun: Handler<'REDEMPTION_RUN_PRICE'> = (state, action) => {
  const run = state.redemptionRuns.find((item) => item.id === action.id);
  const unitClass = state.classes.find((item) => item.id === run?.classId);
  if (!run || !unitClass || run.status !== 'OPEN') return state;
  const holderIds = new Set(
    state.ledger.filter((entry) => entry.classId === run.classId).map((entry) => entry.holderId),
  );
  const classUnits = Array.from(holderIds).reduce(
    (total, holderId) => total + unitsAt(state.ledger, holderId, run.classId, today),
    0,
  );
  const priced = priceRedemptionLines(run.lines, unitClass.unitPrice, classUnits, run.gatePct);
  const next: RegistryState = {
    ...state,
    redemptionRuns: state.redemptionRuns.map((item) =>
      item.id === run.id
        ? { ...item, status: 'PRICED', unitPrice: unitClass.unitPrice, lines: priced.lines }
        : item,
    ),
  };
  return withAudit(
    next,
    'Redemption window closed and priced',
    `${fundCode(state, run.fundId)} ${run.windowDate}`,
    priced.gated
      ? `Gate applied: requests scaled to ${(priced.scale * 100).toFixed(1)}%`
      : `Priced at $${unitClass.unitPrice.toFixed(4)}`,
  );
};

export const submitRedemptionRun: Handler<'REDEMPTION_RUN_SUBMIT'> = (state, action) => {
  const run = state.redemptionRuns.find((item) => item.id === action.id);
  if (!run || run.status !== 'PRICED') return state;
  const total = run.lines.reduce((sum, line) => sum + line.amount, 0);
  let next: RegistryState = {
    ...state,
    redemptionRuns: state.redemptionRuns.map((item) =>
      item.id === run.id ? { ...item, status: 'PENDING_APPROVAL' } : item,
    ),
  };
  next = addApproval(next, {
    kind: 'REDEMPTION_RUN',
    refId: run.id,
    fundId: run.fundId,
    title: `${fundCode(state, run.fundId)} redemptions at the ${run.windowDate} window`,
    summary: `${run.lines.length} redemptions priced at $${(run.unitPrice ?? 1).toFixed(4)}.`,
    amount: total,
  });
  return withAudit(
    next,
    'Redemption run submitted for approval',
    fundCode(state, run.fundId),
    money(total),
  );
};

export const payRedemptionRun: Handler<'REDEMPTION_RUN_PAY'> = (state, action) => {
  const run = state.redemptionRuns.find((item) => item.id === action.id);
  if (!run || run.status !== 'APPROVED') return state;
  const code = fundCode(state, run.fundId);
  let next = appendLedger(
    {
      ...state,
      redemptionRuns: state.redemptionRuns.map((item) =>
        item.id === run.id ? { ...item, status: 'PAID' } : item,
      ),
    },
    run.lines
      .filter((line) => line.unitsRedeemed > 0)
      .map((line) => ({
        fundId: run.fundId,
        classId: run.classId,
        holderId: line.holderId,
        type: 'REDEMPTION' as const,
        effectiveDate: today,
        units: -line.unitsRedeemed,
        amount: line.amount,
        unitPrice: run.unitPrice,
        reference: `REDEMPTION ${run.windowDate}`,
        eventId: run.id,
        memo:
          line.unitsRedeemed < line.unitsRequested
            ? 'Redeemed (scaled back by liquidity gate)'
            : 'Redeemed at window NAV',
        postedBy: actorName(state),
        approvedBy: run.approvedBy,
      })),
  );
  next = raiseHeldPayments(
    next,
    run.fundId,
    `${code} redemption at ${run.windowDate}`,
    run.lines.map((line) => ({ holderId: line.holderId, amount: line.amount })),
  );
  const requestIds = new Set(run.lines.map((line) => line.requestId));
  next = {
    ...next,
    changeRequests: next.changeRequests.map((request) =>
      requestIds.has(request.id) ? { ...request, status: 'COMPLETED' } : request,
    ),
  };
  next = addDocuments(
    next,
    run.lines.map((line) => ({
      holderId: line.holderId,
      fundId: run.fundId,
      kind: 'REDEMPTION_CONFIRMATION' as const,
      name: `${code} redemption confirmation (${run.windowDate})`,
      date: today,
      sizeKb: 70,
      visibleToInvestor: true,
      refId: run.id,
    })),
  );
  next = addCommunication(next, {
    template: 'REDEMPTION_CONFIRMATION',
    subject: `Your ${code} redemption has been paid`,
    audience: `${code} holders redeeming at ${run.windowDate}`,
    fundId: run.fundId,
    holderIds: run.lines.map((line) => line.holderId),
    channels: ['EMAIL', 'PUSH'],
  });
  return withAudit(
    next,
    'Redemptions paid',
    `${code} ${run.windowDate}`,
    `${run.lines.length} payments released`,
  );
};
